import { useEffect, useRef } from 'react';

import { Badge, Button, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import { mdiPhoneInTalk } from '@mdi/js';
import { Icon } from '@mdi/react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Deal } from '../../gql/dealGql';
import { stripDeprecatedFinancialInfoFields } from '../../gql/financialInfoGql';
import {
  NewEstimateCustomerInput,
  NewEstimateUpsertInput,
  useDuplicateDealsLazyQuery,
  useNewEstimateUpsertMutation,
} from '../../gql/generated/graphql';

import Modal from '../shared/Modal';

import { ThunderOutboundCallModalData } from './ThunderOutboundCallModalManager';

import { logger } from '../../libs/Logger';
import {
  formatPhoneNumberForDisplay,
  formatPhoneWithParentheses,
  formatTime,
} from '../../libs/utils';
import { cleanPhoneNumber } from '../../utils/phoneNumbers';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  leadData: ThunderOutboundCallModalData | null;
  onDuplicatesFound?: (deals: Deal[]) => void;
};

// SIP answer already has rep + customer on the line, so this modal has no countdown,
// no "Dial When Ready", and no outcome reporting. Outcome is recorded by leaseend-api's
// call-end webhook posting to tater-thunder's /pending-call/update.
const ThunderInboundCallbackModal = ({ isOpen, onClose, leadData, onDuplicatesFound }: Props) => {
  const history = useHistory();
  const [newEstimateUpsert, { loading: isNewEstimateUpsertLoading }] =
    useNewEstimateUpsertMutation();
  const [checkDuplicateDeals, { loading: duplicateDealsLoading }] = useDuplicateDealsLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const isProcessing = isNewEstimateUpsertLoading || duplicateDealsLoading;
  const hasProcessedRef = useRef(false);

  // Reset the double-click guard between sessions — the modal is always-mounted, so
  // we can't rely on remount to clear hasProcessedRef.
  useEffect(() => {
    if (!isOpen) {
      hasProcessedRef.current = false;
    }
  }, [isOpen]);

  const handleCreateDeal = async () => {
    if (hasProcessedRef.current) {
      return;
    }
    hasProcessedRef.current = true;

    // Retryable failures must keep the modal open + reset the guard so the rep can retry
    // — the customer is on the line and this is the rep's only window into the SMS context.
    const abortAndAllowRetry = () => {
      hasProcessedRef.current = false;
    };

    if (!leadData?.phoneNumber) {
      onClose();
      return;
    }

    const cleanedNumber = cleanPhoneNumber(leadData.phoneNumber);
    if (!cleanedNumber) {
      toast.error('Invalid phone number');
      abortAndAllowRetry();
      return;
    }

    try {
      const { data: dupData, error: dupError } = await checkDuplicateDeals({
        variables: {
          phoneNumber: formatPhoneWithParentheses(cleanedNumber),
          matchBootedDeals: true,
        },
      });

      // Apollo returns non-throwing GraphQL errors via result.error. Stop the create path
      // in that case — creating a new deal without a valid dup check can silently
      // duplicate an existing deal while the customer is on the line.
      if (dupError) {
        logger.error(
          'ThunderInboundCallbackModal.tsx',
          'GraphQL error checking for duplicates',
          null,
          dupError,
        );
        toast.error('Failed to check for duplicates');
        abortAndAllowRetry();
        return;
      }

      if (dupData?.duplicateDeals?.length) {
        // Existing deal(s) found — hand off to the parent's DuplicateDealsModal so the
        // rep can navigate to an existing deal instead of creating a duplicate.
        onClose();
        if (onDuplicatesFound) {
          const validDeals = dupData.duplicateDeals.filter(
            (deal): deal is NonNullable<typeof deal> => deal !== null,
          );
          onDuplicatesFound(validDeals as Deal[]);
        }
        return;
      }
    } catch (networkError) {
      logger.error(
        'ThunderInboundCallbackModal.tsx',
        'Network error checking for duplicates',
        null,
        networkError,
      );
      toast.error('Failed to check for duplicates');
      abortAndAllowRetry();
      return;
    }

    const fullname = leadData?.fullname ?? '';
    const nameParts = fullname.trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    const estimate = new Deal();
    estimate.customer.first_name = firstName;
    estimate.customer.last_name = lastName;
    estimate.customer.phone_number = formatPhoneWithParentheses(cleanedNumber);

    const customerInput: NewEstimateCustomerInput = {
      first_name: estimate.customer.first_name,
      last_name: estimate.customer.last_name,
      phone_number: estimate.customer.phone_number,
      employment: {},
      address: { ...estimate.customer.address },
    };

    const estimateInput: NewEstimateUpsertInput = {
      customer: customerInput,
      car: {
        vin: estimate.car.vin,
        year: estimate.car.year,
        make: estimate.car.make,
        model: estimate.car.model,
        payoff: { ...estimate.car.payoff },
      },
      financial_info: stripDeprecatedFinancialInfoFields({ ...estimate.financial_info }),
      type: estimate.type,
      source: estimate.source,
      state: estimate.state,
      notes: [{ text: 'Thunder Customer Callback Created' }],
    };

    let dealId: number | null = null;
    try {
      const estimateResult = await newEstimateUpsert({
        variables: { newEstimateUpsertInput: estimateInput },
      });
      dealId = estimateResult.data?.newEstimateUpsert?.id ?? null;
    } catch (error) {
      logger.error('ThunderInboundCallbackModal.tsx', 'Error creating estimate', null, error);
      toast.error('Failed to create estimate');
      abortAndAllowRetry();
      return;
    }

    if (dealId == null) {
      toast.error('Failed to create estimate');
      abortAndAllowRetry();
      return;
    }

    history.push(`/deals/${dealId}`);
    onClose();
  };

  const lastAttemptFormatted = leadData?.lastAttemptTimestamp
    ? formatTime(leadData.lastAttemptTimestamp)
    : null;

  return (
    <Modal
      title={leadData?.fullname ? `On The Line: ${leadData.fullname}` : 'On The Line'}
      isOpen={isOpen}
      onClose={onClose}
      canDismiss
      // Stays false so an accidental click while talking to the customer doesn't dismiss.
      closeOnOverlayClick={false}
      size="lg"
      rightButtons={
        <Button
          leftIcon={<Icon path={mdiPhoneInTalk} size="20px" />}
          variant="primary"
          onClick={handleCreateDeal}
          textTransform="uppercase"
          isLoading={isProcessing}
          isDisabled={isProcessing}
        >
          Create Deal
        </Button>
      }
    >
      <VStack spacing={4} align="stretch">
        <Flex justifyContent="flex-start">
          <Badge
            role="status"
            bg="lightGreen.100"
            color="lightGreen.800"
            fontSize="xs"
            textTransform="uppercase"
            px={2}
            py={1}
            borderRadius="md"
          >
            ALEX (AI) CALLBACK
          </Badge>
        </Flex>

        <VStack spacing={1} align="flex-start">
          {leadData?.phoneNumber && (
            <HStack spacing={2} alignItems="center">
              {leadData?.thunderPhoneNumber ? (
                <>
                  <Text fontSize="sm" color="gray.600">
                    Calling
                  </Text>
                  <Text fontWeight="bold" fontSize="sm">
                    {formatPhoneNumberForDisplay(leadData.thunderPhoneNumber)}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    from
                  </Text>
                  <Text fontWeight="bold" fontSize="sm">
                    {formatPhoneNumberForDisplay(leadData.phoneNumber)}
                  </Text>
                </>
              ) : (
                <Text fontWeight="bold" fontSize="sm">
                  {formatPhoneNumberForDisplay(leadData.phoneNumber)}
                </Text>
              )}
            </HStack>
          )}
          {lastAttemptFormatted && (
            <Text fontSize="sm" color="gray.600">
              • Last outbound attempt: {lastAttemptFormatted}
            </Text>
          )}
        </VStack>
      </VStack>
    </Modal>
  );
};

export default ThunderInboundCallbackModal;
